import { orderXClient } from '@/service/client'
import type {
  DataPayload,
  FedwatchProbabilitiesRequest,
  FedwatchProbabilityList,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// ENG-851 — CME FedWatch target-rate probabilities, derived from the 30-Day Fed
// Funds futures strip (ZQ) on GLBX and recomputed every minute.
//
// Note this cannot ride the shared Amberdata analytics pull path: both
// GetAnalyticSnapshot and GetAnalyticRange reject ANALYTICS_TYPE_FEDWATCH with a
// 400 pointing here, because one snapshot is many rows and a row-level `limit`
// would slice a probability grid in half.

export async function fetchFedwatchProbabilities(
  params: FedwatchProbabilitiesRequest,
  signal?: AbortSignal
): Promise<FedwatchProbabilityList> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        fedwatchProbabilities: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    // Only a missing oneof arm is a failure. An EMPTY `items` array is a perfectly
    // good response: it is what an exchange other than GLBX returns (accepted, not
    // an error), and what a window with no grid published in the last 7 days
    // returns. Both must reach the caller as success and render as an empty state.
    const list = data.fedwatchProbabilities
    if (!list) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch FedWatch probabilities',
      } satisfies ErrorResponse)
    }

    return list
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
